<template>
  <v-card class="mb-4" :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''">
    <v-card-title class="subtitle-1 text-uppercase font-weight-medium">
      <v-icon left>mdi-receipt</v-icon>
      {{ $t('inventory.purchase_invoices.title') }}
    </v-card-title>
    <v-card-text class="pb-0">
      <v-row dense align="center">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="search"
            :label="$t('inventory.purchase_invoices.search')"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
          />
        </v-col>
        <v-col cols="12" sm="6" md="auto">
          <v-checkbox
            v-model="onlyUnbooked"
            :label="$t('inventory.purchase_invoices.only_unbooked')"
            hide-details
            dense
            class="mt-0"
          />
        </v-col>
        <v-spacer />
        <v-col v-if="$isTenantAdmin" cols="12" sm="auto">
          <v-btn color="primary" outlined :block="$vuetify.breakpoint.xsOnly" :loading="syncing" @click="refresh">
            <v-icon left>mdi-refresh</v-icon>
            {{ $t('sites.purchase_invoices.refresh') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <netvisor-purchase-invoice-table
        :invoices="filteredInvoices"
        :loading="loading"
        :allocated-label="$t('inventory.purchase_invoices.warehouse_amount')"
        :no-data-text="onlyUnbooked && invoices.length ? $t('inventory.purchase_invoices.no_unbooked') : $t('inventory.purchase_invoices.no_invoices')"
        bookable
        @book="(invoice, line) => $refs.bookingDialog.open(invoice, line)"
        @set-status="setStatus"
        @clear-status="clearStatus"
      />
    </v-card-text>

    <purchase-line-booking-dialog ref="bookingDialog" @saved="onBooked" />

    <v-dialog v-model="handledDialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '450'" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title class="white--text">{{ $t('inventory.purchase_invoices.mark_handled') }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark :aria-label="$t('close')" @click="handledDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <v-text-field v-model="handledNote" :label="$t('inventory.purchase_invoices.handled_note')" outlined dense hide-details />
        </v-card-text>
        <v-divider />
        <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
          <v-spacer />
          <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="handledDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            :block="$vuetify.breakpoint.xsOnly"
            :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
            @click="saveHandled"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import netvisorApi from '@/api/netvisor';
import NetvisorPurchaseInvoiceTable from '@/components/Netvisor/NetvisorPurchaseInvoiceTable';
import PurchaseLineBookingDialog from '@/components/Inventory/PurchaseLineBookingDialog';

/**
 * Varastonhallinnan ostolaskut: Netvisorissa kohdelaskennalla "92 Varasto" merkityt laskurivit.
 * Haku on selainpuolinen (toimittaja, laskunumero, rivien selite/tuote).
 */
export default {
  name: 'InventoryPurchaseInvoices',
  components: { NetvisorPurchaseInvoiceTable, PurchaseLineBookingDialog },
  data() {
    return {
      invoices: [],
      loading: false,
      syncing: false,
      search: '',
      onlyUnbooked: false,
      handledDialog: false,
      handledNote: '',
      handledTarget: null,
    };
  },
  computed: {
    filteredInvoices() {
      const term = (this.search || '').trim().toLowerCase();
      let invoices = this.invoices;
      if (this.onlyUnbooked) {
        invoices = invoices
          .map(inv => ({ ...inv, lines: (inv.lines || []).filter(l => l.bookingStatus === 'NOT_BOOKED') }))
          .filter(inv => inv.lines.length > 0);
      }
      if (!term) return invoices;
      return invoices.filter(inv => this.invoiceText(inv).includes(term));
    },
  },
  methods: {
    invoiceText(inv) {
      const lineTexts = (inv.lines || []).map(l => [l.description, l.productCode, l.productName].filter(Boolean).join(' '));
      return [inv.vendorName, inv.invoiceNumber, ...lineTexts].filter(Boolean).join(' ').toLowerCase();
    },

    async loadInvoices() {
      this.loading = true;
      try {
        this.invoices = await netvisorApi.getWarehousePurchaseInvoices();
      } catch (error) {
        console.error('Error loading warehouse purchase invoices:', error);
        this.showError(this.$t('sites.purchase_invoices.load_failed'));
      } finally {
        this.loading = false;
      }
    },

    // Kirjaus päivittää nimikkeen hinnan, joten varastonäkymä lataa nimikkeet uudelleen
    onBooked() {
      this.loadInvoices();
      this.$emit('booked');
    },

    async refresh() {
      this.syncing = true;
      try {
        await netvisorApi.syncPurchaseInvoices();
        await this.loadInvoices();
        this.showSuccess(this.$t('sites.purchase_invoices.refreshed'));
      } catch (error) {
        console.error('Error syncing purchase invoices:', error);
        this.showError(this.$t('sites.purchase_invoices.refresh_failed'));
      } finally {
        this.syncing = false;
      }
    },

    setStatus(invoice, line, status) {
      if (status === 'HANDLED') {
        this.handledTarget = { invoice, line };
        this.handledNote = '';
        this.handledDialog = true;
        return;
      }
      this.saveStatus(invoice, line, status, null);
    },

    saveHandled() {
      this.handledDialog = false;
      this.saveStatus(this.handledTarget.invoice, this.handledTarget.line, 'HANDLED', this.handledNote);
    },

    async saveStatus(invoice, line, status, note) {
      try {
        await netvisorApi.setWarehouseLineStatus(invoice.netvisorKey, line.netvisorLineKey, status, note);
        await this.loadInvoices();
      } catch (error) {
        console.error('Error saving purchase line status:', error);
        this.showError(this.$t('inventory.purchase_invoices.status_failed'));
      }
    },

    async clearStatus(invoice, line) {
      try {
        await netvisorApi.clearWarehouseLineStatus(invoice.netvisorKey, line.netvisorLineKey);
        await this.loadInvoices();
      } catch (error) {
        console.error('Error clearing purchase line status:', error);
        this.showError(this.$t('inventory.purchase_invoices.status_failed'));
      }
    },

    showSuccess(message) {
      this.$store.dispatch('showNotification', { color: 'success', message, timeOut: 2000 });
    },

    showError(message) {
      this.$store.dispatch('showNotification', { color: 'error', message, timeOut: 3000 });
    },
  },
  mounted() {
    this.loadInvoices();
  },
};
</script>
